import { useOutletContext } from "react-router-dom";
import { BarChart3, ClipboardList, Megaphone, Users } from "lucide-react";
import Seo from "@/components/site/Seo";
import { Cta, EmptyState, Notice, PageHero, Reveal, SectionHeading } from "@/components/site/kit";
import useFetch from "@/hooks/useFetch";
import { indexBy } from "@/components/site/cards";
import { PlayerTile } from "./TeamDetail";

const STEPS = [
  { t: "Register interest", d: "Submit your details and a photograph through the official registration form." },
  { t: "Eligibility review", d: "The league team reviews registrations against the official eligibility criteria once published." },
  { t: "Trials", d: "Trial dates, locations and formats will be announced on this page and via official channels." },
  { t: "Selection", d: "Selected players are allocated to franchises through the official league process." },
];

const STAT_COLS = ["Matches", "Runs", "Highest", "Strike rate", "Wickets", "Best bowling", "Economy"];

export default function Players() {
  const { settings = {} } = useOutletContext() || {};
  const { data: players } = useFetch("/players", []);
  const { data: teams } = useFetch("/teams", []);
  const tMap = indexBy(teams);
  const bySquad = (players || []).reduce((acc, p) => { (acc[p.team_id || "unassigned"] ||= []).push(p); return acc; }, {});

  return (
    <>
      <Seo title="Players" description="Register your interest as a player in the Telangana–Andhra T10 League. Eligibility, trials and player selection information." />
      <PageHero testid="players-hero" eyebrow="Players" title="Your Game. Your Opportunity." image="/images/batsman.jpg">
        <p>Be part of a new regional platform for tennis-ball cricket talent. Register your interest and follow official announcements about eligibility, trials and player selection.</p>
        <div className="mt-8"><Cta to="/players/register" variant="accent" testid="players-register-btn">Register as a Player</Cta></div>
      </PageHero>

      <section className="container-x py-20">
        <SectionHeading eyebrow="Pathway" title="How Player Selection Works" />
        <ol className="mt-10 grid gap-4 md:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.07}>
              <li className="h-full rounded-md border border-white/10 bg-[#112240] p-6">
                <span className="font-score text-4xl font-bold text-[#00E5FF]">0{i + 1}</span>
                <h3 className="mt-3 font-display text-2xl uppercase">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-400">{s.d}</p>
              </li>
            </Reveal>
          ))}
        </ol>
        <div className="mt-8"><Notice testid="no-guarantee-notice">Registering your interest does not guarantee selection, trial invitations or a place in any franchise squad.</Notice></div>
      </section>

      <section id="eligibility" className="container-x grid gap-6 pb-20 lg:grid-cols-2">
        <div data-testid="eligibility-section" className="rounded-md border border-white/10 bg-[#112240] p-8">
          <ClipboardList className="h-7 w-7 text-[#00E5FF]" />
          <h2 className="mt-4 font-display text-3xl uppercase">Player Eligibility</h2>
          <p className="mt-3 whitespace-pre-line text-sm text-slate-300">{settings.eligibility || "Official eligibility criteria (age, residency and registration requirements) will be published here by CGN Sports. Please check back for updates."}</p>
        </div>
        <div id="trials" data-testid="trials-section" className="rounded-md border border-white/10 bg-[#112240] p-8">
          <Megaphone className="h-7 w-7 text-[#FF5722]" />
          <h2 className="mt-4 font-display text-3xl uppercase">Trial Announcements</h2>
          <p className="mt-3 whitespace-pre-line text-sm text-slate-300">{settings.trial_announcement || "No trials have been announced yet. Trial dates and locations will be published here once confirmed."}</p>
        </div>
      </section>

      <section id="squads" className="bg-[#071325] py-20">
        <div className="container-x">
          <SectionHeading eyebrow="Profiles & Squads" title="Player Profiles" />
          <div className="mt-10">
            {players?.length ? Object.entries(bySquad).map(([tid, list]) => (
              <div key={tid} className="mb-10">
                <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#00E5FF]">{tMap[tid]?.name || "Squad allocation pending"}</h3>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">{list.map((p) => <PlayerTile key={p.id} p={p} />)}</div>
              </div>
            )) : <EmptyState testid="players-empty" icon={Users} title="Player profiles coming soon">Only approved player profiles are published. Squads will be listed here after official selection.</EmptyState>}
          </div>
        </div>
      </section>

      <section id="stats" className="container-x py-20">
        <SectionHeading eyebrow="Statistics" title="Player Statistics" />
        <div className="mt-8 overflow-x-auto rounded-md border border-white/10">
          <table data-testid="player-stats-framework" className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-[#112240] text-[11px] uppercase tracking-widest text-slate-400">
              <tr><th className="p-4">Player</th>{STAT_COLS.map((c) => <th key={c} className="p-4">{c}</th>)}</tr>
            </thead>
            <tbody>
              <tr><td colSpan={8} className="p-8 text-center text-slate-400"><BarChart3 className="mx-auto mb-2 h-6 w-6" />Statistics will be available once matches are played.</td></tr>
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
